import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import {
	getContractRequiredMinimumRefinement,
	refineContractRequiredMinimum,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import {
	type KeyFieldsLockOptions,
	lockableField,
} from "web_myplann/Shared/Validation/lockableField";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { refineDateOrder } from "web_myplann/Shared/Validation/refineDateOrder";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const getBuildingSavingsBaseShape = (
	t: TranslateFunction,
	{ lockKeyFields = false }: KeyFieldsLockOptions = {},
) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return {
		name: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
		),
		contractNumber: lockableField(
			trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			lockKeyFields,
		),
		producer: lockableField(
			trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			lockKeyFields,
		),
		product: lockableField(
			trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			lockKeyFields,
		),
		signedAt: lockableField(
			optionalDateAsString(DateFormat.Iso),
			lockKeyFields,
		),
		startAt: lockableField(optionalDateAsString(DateFormat.Iso), lockKeyFields),
		endAt: lockableField(optionalDateAsString(DateFormat.Iso), lockKeyFields),
		payment: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		paymentFrequency: z.nativeEnum(ClientPortfolioPaymentFrequencyEnum),
		appreciation: optionalPositiveFloat(
			ValidationLimits.Percent.Max,
			positiveNumberMessages,
		),
		targetAmount: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
	};
};

const getBuildingSavingsValuesShape = (t: TranslateFunction) => {
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage: t(Resources.Portfolio.AddShared.Validations.maxValue),
	};

	return {
		currentValue: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		totalInvested: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		valueDate: optionalDateAsString(
			DateFormat.Iso,
			t(Resources.Portfolio.AddShared.Validations.dateInFuture),
		),
	};
};

const getDateOrderRefinement = (t: TranslateFunction) =>
	refineDateOrder({
		endBeforeSigned: t(
			Resources.Portfolio.AddShared.Validations.endBeforeSigned,
		),
	});

export const getBuildingSavingsCreateFormSchema = (t: TranslateFunction) =>
	z
		.object({
			...getBuildingSavingsBaseShape(t),
			...getBuildingSavingsValuesShape(t),
		})
		.superRefine(getDateOrderRefinement(t))
		.superRefine(refineContractRequiredMinimum);

export const getBuildingSavingsEditFormSchema = (
	t: TranslateFunction,
	{ lockKeyFields = false }: KeyFieldsLockOptions = {},
) =>
	z
		.object(getBuildingSavingsBaseShape(t, { lockKeyFields }))
		.superRefine(getDateOrderRefinement(t))
		.superRefine(getContractRequiredMinimumRefinement({ lockKeyFields }));

export type BuildingSavingsCreateFormInput = z.input<
	ReturnType<typeof getBuildingSavingsCreateFormSchema>
>;
export type BuildingSavingsCreateFormOutput = z.output<
	ReturnType<typeof getBuildingSavingsCreateFormSchema>
>;

export type BuildingSavingsEditFormInput = z.input<
	ReturnType<typeof getBuildingSavingsEditFormSchema>
>;
export type BuildingSavingsEditFormOutput = z.output<
	ReturnType<typeof getBuildingSavingsEditFormSchema>
>;
